// src/pages/EditRiskAssessment.tsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

// Helper: empty ⇒ null, else Number
const convertToNumberOrNull = (value: string) =>
  value.trim() === "" ? null : Number(value);

const EditRiskAssessment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    gdpValue: "",
    gdpUnit: "billion",
    gdpGrowthRate: "",
    inflationRate: "",
    unemploymentRate: "",
    publicDebt: "",
    politicalStabilityIndex: "",
    recentPoliticalEvents: "",
    riskOfArmedConflict: "None",
    naturalDisasters: "",
    agriculturalLandImpact: "",
    weatherVulnerabilityIndex: "",
    frostImpact: "None",
    frostEvents: ""
  });

  useEffect(() => {
    fetch(`/api/countries/${id}/risk-assessment`)
      .then((res) => res.json())
      .then((data) => {
        // split GDP into value + unit
        let gdpValue = "";
        let gdpUnit: "million" | "billion" | "trillion" = "billion";
        if (data.gdp != null) {
          const n = Number(data.gdp);
          if (n >= 1e12) {
            gdpUnit = "trillion";
            gdpValue = String(n / 1e12);
          } else if (n >= 1e9) {
            gdpUnit = "billion";
            gdpValue = String(n / 1e9);
          } else {
            gdpUnit = "million";
            gdpValue = String(n / 1e6);
          }
        }
        setFormData({
          gdpValue,
          gdpUnit,
          gdpGrowthRate: data.gdp_growth_rate ?? "",
          inflationRate: data.inflation_rate ?? "",
          unemploymentRate: data.unemployment_rate ?? "",
          publicDebt: data.public_debt_percent ?? "",
          politicalStabilityIndex: data.political_stability_index ?? "",
          recentPoliticalEvents: data.recent_political_events ?? "",
          riskOfArmedConflict: data.risk_of_armed_conflict ?? "None",
          naturalDisasters: data.natural_disasters ?? "",
          agriculturalLandImpact: data.agricultural_land_impact ?? "",
          weatherVulnerabilityIndex: data.weather_vulnerability_index ?? "",
          frostImpact: data.frost_impact ?? "None",
          frostEvents: data.frost_events ?? ""
        });
      })
      .catch((err) => console.error("Error fetching risk assessment", err));
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent< HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const multipliers: Record<string, number> = {
      million: 1e6,
      billion: 1e9,
      trillion: 1e12
    };
    const raw = formData.gdpValue.trim();
    const gdp = raw === "" ? null : Number(raw) * multipliers[formData.gdpUnit];

    const payload = {
      gdp,
      gdpGrowthRate: convertToNumberOrNull(formData.gdpGrowthRate),
      inflationRate: convertToNumberOrNull(formData.inflationRate),
      unemploymentRate: convertToNumberOrNull(formData.unemploymentRate),
      publicDebt: convertToNumberOrNull(formData.publicDebt),
      politicalStabilityIndex: convertToNumberOrNull(formData.politicalStabilityIndex),
      recentPoliticalEvents: formData.recentPoliticalEvents,
      riskOfArmedConflict: formData.riskOfArmedConflict,
      naturalDisasters: formData.naturalDisasters,
      agriculturalLandImpact: convertToNumberOrNull(formData.agriculturalLandImpact),
      weatherVulnerabilityIndex: convertToNumberOrNull(formData.weatherVulnerabilityIndex),
      frostImpact: formData.frostImpact,
      frostEvents: formData.frostEvents,
      sources: null  // hidden for now
    };

    fetch(`/api/countries/${id}/risk-assessment`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
      .then(() => navigate(`/country/${id}`))
      .catch((err) => console.error("Error updating risk assessment", err));
  };

  // Style objects
  const containerStyle: React.CSSProperties = {
    backgroundColor: "#F0F5F0",
    minHeight: "100vh",
    padding: "20px"
  };
  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "800px",
    margin: "30px auto",
    padding: "30px"
  };
  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px"
  };
  const sectionHeaderStyle: React.CSSProperties = {
    color: "#e41e26",
    borderBottom: "1px solid #e41e26",
    paddingBottom: "5px",
    marginTop: "15px"
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold"
  };
  const inputStyle: React.CSSProperties = {
    padding: "8px",
    marginBottom: "10px",
    borderRadius: "4px",
    border: "1px solid #ccc"
  };
  const buttonStyle: React.CSSProperties = {
    padding: "5px 10px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "10px"
  };

  return (
    <div style={containerStyle}>
      <Navbar />
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Risk Assessment</h1>
        <form onSubmit={handleSubmit}>

          <h3 style={sectionHeaderStyle}>Economic Stability</h3>

          <label style={labelStyle}>GDP:</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              type="number"
              name="gdpValue"
              style={{ ...inputStyle, flex: 1, width: "auto" }}
              value={formData.gdpValue}
              onChange={handleChange}
              placeholder="1.23"
            />
            <select
              name="gdpUnit"
              value={formData.gdpUnit}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="million">Million</option>
              <option value="billion">Billion</option>
              <option value="trillion">Trillion</option>
            </select>
          </div>

          <label style={labelStyle}>GDP Growth Rate (%):</label>
          <input
            type="number"
            name="gdpGrowthRate"
            style={inputStyle}
            value={formData.gdpGrowthRate}
            onChange={handleChange}
            placeholder="e.g. 2.5"
          />

          <label style={labelStyle}>Inflation Rate (%):</label>
          <input
            type="number"
            name="inflationRate"
            style={inputStyle}
            value={formData.inflationRate}
            onChange={handleChange}
            placeholder="e.g. 1.8"
          />

          <label style={labelStyle}>Unemployment Rate (%):</label>
          <input
            type="number"
            name="unemploymentRate"
            style={inputStyle}
            value={formData.unemploymentRate}
            onChange={handleChange}
            placeholder="e.g. 5.2"
          />

          <label style={labelStyle}>Public Debt as % of GDP:</label>
          <input
            type="number"
            name="publicDebt"
            style={inputStyle}
            value={formData.publicDebt}
            onChange={handleChange}
            placeholder="e.g. 60"
          />

          <h3 style={sectionHeaderStyle}>Political Environment</h3>

          <label style={labelStyle}>Political Stability Index (decimal):</label>
          <input
            type="number"
            name="politicalStabilityIndex"
            step="0.01"
            style={inputStyle}
            value={formData.politicalStabilityIndex}
            onChange={handleChange}
            placeholder="e.g. 1.23"
          />

          <label style={labelStyle}>Recent Political Events:</label>
          <textarea
            name="recentPoliticalEvents"
            style={inputStyle}
            value={formData.recentPoliticalEvents}
            onChange={handleChange}
          />

          <label style={labelStyle}>Risk of Violence:</label>
          <select
            name="riskOfArmedConflict"
            value={formData.riskOfArmedConflict}
            onChange={handleChange}
            style={inputStyle}
          >
            <option>None</option>
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
            <option>Active Conflict</option>
          </select>

          <h3 style={sectionHeaderStyle}>Environmental Factors Affecting Agriculture</h3>

          <label style={labelStyle}>Natural Disasters:</label>
          <textarea
            name="naturalDisasters"
            style={inputStyle}
            value={formData.naturalDisasters}
            onChange={handleChange}
          />

          <label style={labelStyle}>Agricultural Land Impacted (%):</label>
          <input
            type="number"
            name="agriculturalLandImpact"
            style={inputStyle}
            value={formData.agriculturalLandImpact}
            onChange={handleChange}
            placeholder="e.g. 12"
          />

          <label style={labelStyle}>Weather Vulnerability Index (whole number):</label>
          <input
            type="number"
            name="weatherVulnerabilityIndex"
            step="1"
            style={inputStyle}
            value={formData.weatherVulnerabilityIndex}
            onChange={handleChange}
          />

          <label style={labelStyle}>Frost Impact:</label>
          <select
            name="frostImpact"
            value={formData.frostImpact}
            onChange={handleChange}
            style={inputStyle}
          >
            <option>None</option>
            <option>Low</option>
            <option>Medium</option>
            <option>High</option>
            <option>Extreme</option>
          </select>

          <label style={labelStyle}>Recent Frost Events:</label>
          <input
            type="text"
            name="frostEvents"
            style={inputStyle}
            value={formData.frostEvents}
            onChange={handleChange}
            placeholder="e.g. 3 mild, 1 severe"
          />

          <div style={{ marginTop: 20 }}>
            <button type="submit" style={buttonStyle}>Save</button>
            <button
              type="button"
              style={buttonStyle}
              onClick={() => navigate(`/country/${id}`)}
            >
              Cancel
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditRiskAssessment;
